import { UseMutationOptions, useQueryClient } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  DuplicateNoticeListMutation,
  DuplicateNoticeListMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { EventData, EventNames, useTracking } from "lib/tracking";
import { useGraphQlMutation } from "../../useGraphQlClient";
import { NoticeListIdentity } from "./useNoticeLists";

const duplicateNoticeListMutation = graphql(`
  mutation duplicateNoticeList($input: DuplicateNoticeListInput!) {
    duplicateNoticeList(input: $input) {
      id
      name
    }
  }
`);

/** `list` is the source list; the response only describes the copy. */
type TrackingContext = {
  list: NoticeListIdentity;
  trackingData?: EventData;
};

export function useDuplicateNoticeList(
  { list, trackingData }: TrackingContext,
  options?: UseMutationOptions<
    DuplicateNoticeListMutation,
    unknown,
    DuplicateNoticeListMutationVariables,
    unknown
  >,
) {
  const { logEvent } = useTracking();
  const queryClient = useQueryClient();
  return useGraphQlMutation(duplicateNoticeListMutation, {
    ...options,
    onSuccess: async (data, vars, ctx) => {
      await queryClient.invalidateQueries(["noticeLists"]);
      const newList = data.duplicateNoticeList;
      logEvent(EventNames.listDuplicated, {
        ...trackingData,
        "Existing List name": list.name,
        "Existing List id": list.id,
        "New List name": newList.name,
        "New List id": newList.id,
      });
      options?.onSuccess?.(data, vars, ctx);
    },
  });
}
